import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { gastosDiariosAPI, proveedoresAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

// Gastos diarios (mig 048) — módulo SEPARADO de caja chica: no toca ninguna caja.
// Sin comprobante => motivo obligatorio (mismo mínimo que el backend y el CHECK de la BD).
const MIN_MOTIVO = 10;
const MAX_ARCHIVOS = 5;
const MAX_MB = 10;
const TIPOS_ARCHIVO = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const METODOS_PAGO = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'otro', label: 'Otro' }
];
const PROVEEDOR_OTRO = '__otro__';

// Fecha local (Ecuador) en YYYY-MM-DD, sin pasar por UTC
const fechaLocal = (d = new Date()) => {
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};
const primeroDelMes = () => { const d = new Date(); return fechaLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };
const haceDias = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return fechaLocal(d); };
const fmtFecha = (f) => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '');
const fmtFechaHora = (f) => (f ? new Date(f).toLocaleString('es-EC') : '');
// Largo en caracteres reales (igual que el backend y length() de PostgreSQL)
const largo = (t) => [...String(t || '').trim()].length;
// Monto: acepta coma o punto decimal, máximo 2 decimales
const parsearMonto = (txt) => {
  const t = String(txt || '').trim().replace(',', '.');
  return /^\d+(\.\d{1,2})?$/.test(t) ? Number(t) : NaN;
};
const labelMetodo = (m) => METODOS_PAGO.find(x => x.value === m)?.label || m;

// Los errores de peticiones con responseType 'blob' llegan como Blob
const mensajeError = async (err, porDefecto) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).error || porDefecto; } catch (e) { return porDefecto; }
  }
  return data?.error || porDefecto;
};

const FORM_VACIO = () => ({
  fecha: fechaLocal(), concepto: '', monto: '', categoria_id: '', proveedor_sel: '',
  proveedor_nombre: '', numero_documento: '', metodo_pago: 'efectivo', descripcion: '',
  motivo_sin_comprobante: ''
});

// Celular/tablet: el input con `capture` abre la cámara nativa del teléfono.
// PC: `capture` se ignora, así que se usa la webcam con getUserMedia (requiere HTTPS).
const esDispositivoTactil = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
const puedeUsarWebcam = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

// Fotos grandes de celular (p. ej. 50 MP, >10MB) o en formato no admitido (HEIC) se convierten en el
// navegador a JPEG de máx. 2048 px — el servidor igual las deja en 2048 px. Si no se puede, va el original
// (y la validación de tipo/tamaño lo rechaza con su mensaje).
const reducirSiEsGrande = async (file) => {
  const tipoNoAdmitido = !TIPOS_ARCHIVO.includes(file.type); // p. ej. HEIC de iPhone: Safari sí lo decodifica
  if (!file.type.startsWith('image/') || (file.size <= 2 * 1024 * 1024 && !tipoNoAdmitido)
      || typeof createImageBitmap !== 'function') return file;
  try {
    const bmp = await createImageBitmap(file);
    const escala = Math.min(1, 2048 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * escala);
    canvas.height = Math.round(bmp.height * escala);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob) return file;
    const nombre = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nombre, { type: 'image/jpeg' });
  } catch (e) {
    return file;
  }
};

const CamaraModal = ({ onCaptura, onCerrar }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState('environment');

  useEffect(() => {
    let cancelado = false;
    setListo(false);
    setError(null);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false
        });
        if (cancelado) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setListo(true);
      } catch (e) {
        if (cancelado) return;
        setError(e?.name === 'NotAllowedError'
          ? 'El navegador no tiene permiso para usar la cámara. Permítelo en el candado de la barra de direcciones y vuelve a intentar.'
          : 'No se encontró una cámara disponible en este equipo.');
      }
    })();
    return () => {
      cancelado = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
  }, [facing]);

  const capturar = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d').drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('No se pudo capturar la foto'); return; }
      onCaptura(new File([blob], `foto-comprobante-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1065 }} />
      <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1070 }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-camera me-2"></i>Foto del comprobante</h5>
              <button className="btn-close" onClick={onCerrar} />
            </div>
            <div className="modal-body text-center">
              {error ? (
                <div className="alert alert-warning text-start mb-0">{error}</div>
              ) : (
                <>
                  <video ref={videoRef} playsInline muted className="w-100 rounded bg-dark" style={{ maxHeight: '60vh' }} />
                  {!listo && <div className="py-3"><span className="spinner-border spinner-border-sm me-2"></span>Abriendo la cámara…</div>}
                </>
              )}
            </div>
            <div className="modal-footer">
              {!error && (
                <button className="btn btn-outline-secondary me-auto" onClick={() => setFacing(f => (f === 'environment' ? 'user' : 'environment'))}>
                  <i className="bi bi-arrow-repeat me-1"></i>Cambiar cámara
                </button>
              )}
              <button className="btn btn-secondary" onClick={onCerrar}>Cancelar</button>
              {!error && (
                <button className="btn btn-primary" onClick={capturar} disabled={!listo}>
                  <i className="bi bi-camera-fill me-1"></i>Tomar foto
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const GastosDiarios = () => {
  const { formatCurrency } = useApp();
  const { esAdminOGerente, modoEspectador } = useAuth();
  const autorizado = esAdminOGerente();

  const [gastos, setGastos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [porCategoria, setPorCategoria] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  const [filtros, setFiltros] = useState({
    fecha_desde: primeroDelMes(), fecha_hasta: fechaLocal(), categoria_id: '', comprobante: '', estado: ''
  });
  const [buscarInput, setBuscarInput] = useState('');
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);

  // Nuevo gasto
  const [showNuevo, setShowNuevo] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [archivos, setArchivos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const archivosRef = useRef(null);

  // Detalle
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [preview, setPreview] = useState(null); // { compId, url, tipo }
  const [motivoAnular, setMotivoAnular] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const agregarRef = useRef(null);
  const camaraNuevoRef = useRef(null);   // input con capture (celular) — registrar
  const camaraDetalleRef = useRef(null); // input con capture (celular) — adjuntar después
  const [camaraDestino, setCamaraDestino] = useState(null); // 'nuevo' | 'detalle' (webcam PC)
  const detalleReqRef = useRef(0); // descarta respuestas viejas al abrir otro gasto o cerrar

  // Búsqueda con espera para no consultar en cada tecla
  useEffect(() => {
    const t = setTimeout(() => { setBuscar(buscarInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [buscarInput]);

  const cargar = useCallback(async () => {
    if (!autorizado) return;
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      if (buscar) params.buscar = buscar;
      const res = await gastosDiariosAPI.getAll(params);
      if (res.data?.success) {
        setGastos(res.data.data || []);
        setTotales(res.data.totales || null);
        setPorCategoria(res.data.por_categoria || []);
        setPagination(res.data.pagination || { total: 0, page: 1, pages: 1 });
      }
    } catch (err) {
      toast.error(await mensajeError(err, 'Error al cargar los gastos diarios'));
      setTotales(null);
    } finally {
      setLoading(false);
    }
  }, [filtros, buscar, page, autorizado]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!autorizado) return;
    gastosDiariosAPI.getCategorias()
      .then(r => { if (r.data?.success) setCategorias(r.data.data || []); })
      .catch(() => {});
    proveedoresAPI.getAll({ limit: 1000 })
      .then(r => { if (r.data?.success) setProveedores(r.data.data || []); })
      .catch(() => {});
  }, [autorizado]);

  // Liberar el blob de la vista previa al cerrarla o cambiarla
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const setFiltro = (k, v) => { setFiltros(f => ({ ...f, [k]: v })); setPage(1); };
  const setRango = (desde, hasta) => { setFiltros(f => ({ ...f, fecha_desde: desde, fecha_hasta: hasta })); setPage(1); };

  // ─── Archivos ───
  const validarArchivos = (lista, yaHay) => {
    const nuevos = Array.from(lista || []);
    if (yaHay + nuevos.length > MAX_ARCHIVOS) {
      toast.error(`Máximo ${MAX_ARCHIVOS} comprobantes por gasto`);
      return null;
    }
    for (const f of nuevos) {
      if (!TIPOS_ARCHIVO.includes(f.type)) { toast.error(`"${f.name}": use JPG, PNG, WEBP o PDF`); return null; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`"${f.name}" supera ${MAX_MB}MB`); return null; }
    }
    return nuevos;
  };

  const onElegirArchivos = async (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = '';
    const preparados = await Promise.all(lista.map(reducirSiEsGrande));
    const nuevos = validarArchivos(preparados, archivos.length);
    if (nuevos) setArchivos(a => [...a, ...nuevos]);
  };

  // ─── Nuevo gasto ───
  const abrirNuevo = () => { setForm(FORM_VACIO()); setArchivos([]); setShowNuevo(true); };

  const montoNum = parsearMonto(form.monto);
  const montoInvalido = form.monto !== '' && !(Number.isFinite(montoNum) && montoNum > 0);
  const motivoOk = archivos.length > 0 || largo(form.motivo_sin_comprobante) >= MIN_MOTIVO;
  const formValido = form.fecha && form.fecha <= fechaLocal() && form.concepto.trim() &&
    Number.isFinite(montoNum) && montoNum > 0 && form.metodo_pago && motivoOk;

  const guardar = async () => {
    if (!formValido || guardando) return;
    setGuardando(true);
    try {
      const datos = {
        fecha: form.fecha,
        concepto: form.concepto.trim(),
        monto: String(form.monto).trim().replace(',', '.'),
        categoria_id: form.categoria_id,
        metodo_pago: form.metodo_pago,
        numero_documento: form.numero_documento.trim(),
        descripcion: form.descripcion.trim()
      };
      if (form.proveedor_sel === PROVEEDOR_OTRO) datos.proveedor_nombre = form.proveedor_nombre.trim();
      else if (form.proveedor_sel) datos.proveedor_id = form.proveedor_sel;
      if (archivos.length === 0) datos.motivo_sin_comprobante = form.motivo_sin_comprobante.trim();

      const res = await gastosDiariosAPI.create(datos, archivos);
      if (res.data?.success) {
        toast.success(res.data.message || 'Gasto registrado');
        setShowNuevo(false);
        if (page !== 1) setPage(1); // el efecto recarga al cambiar de página
        else cargar();
      }
    } catch (err) {
      toast.error(await mensajeError(err, 'Error al registrar el gasto'));
    } finally {
      setGuardando(false);
    }
  };

  // ─── Detalle ───
  const abrirDetalle = async (id, conservarPreview = false) => {
    const reqId = ++detalleReqRef.current;
    if (!conservarPreview) setPreview(null);
    setMotivoAnular('');
    setLoadingDetalle(true);
    setDetalle(d => (d && d.id === id ? d : { id }));
    try {
      const res = await gastosDiariosAPI.getById(id);
      if (reqId !== detalleReqRef.current) return;
      if (res.data?.success) setDetalle(res.data.data);
    } catch (err) {
      if (reqId !== detalleReqRef.current) return;
      toast.error(await mensajeError(err, 'No se pudo cargar el gasto'));
      setDetalle(null);
    } finally {
      if (reqId === detalleReqRef.current) setLoadingDetalle(false);
    }
  };

  const cerrarDetalle = () => { detalleReqRef.current++; setPreview(null); setDetalle(null); };

  const verComprobante = async (comp) => {
    if (preview?.compId === comp.id) { setPreview(null); return; }
    const reqId = detalleReqRef.current;
    try {
      const res = await gastosDiariosAPI.descargarComprobante(detalle.id, comp.id);
      if (reqId !== detalleReqRef.current) return; // se cerró o cambió el detalle
      const url = URL.createObjectURL(new Blob([res.data], { type: comp.archivo_tipo }));
      setPreview({ compId: comp.id, url, tipo: comp.archivo_tipo });
    } catch (err) {
      toast.error(await mensajeError(err, 'No se pudo abrir el comprobante'));
    }
  };

  const descargarComprobante = async (comp) => {
    try {
      const res = await gastosDiariosAPI.descargarComprobante(detalle.id, comp.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: comp.archivo_tipo }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detalle.codigo}-${comp.archivo_nombre_original}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(await mensajeError(err, 'No se pudo descargar el comprobante'));
    }
  };

  const onAgregarComprobantes = async (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = '';
    subirAlDetalle(await Promise.all(lista.map(reducirSiEsGrande)));
  };

  const subirAlDetalle = async (lista) => {
    const nuevos = validarArchivos(lista, detalle?.comprobantes?.length || 0);
    if (!nuevos || nuevos.length === 0) return;
    setSubiendo(true);
    try {
      const res = await gastosDiariosAPI.agregarComprobantes(detalle.id, nuevos);
      if (res.data?.success) {
        toast.success(res.data.message || 'Comprobante adjuntado');
        await abrirDetalle(detalle.id);
        cargar();
      }
    } catch (err) {
      toast.error(await mensajeError(err, 'No se pudo adjuntar el comprobante'));
    } finally {
      setSubiendo(false);
    }
  };

  // Tomar foto: en celular abre la cámara nativa; en PC, la webcam en un modal
  const tomarFoto = (destino) => {
    if (esDispositivoTactil() || !puedeUsarWebcam()) {
      (destino === 'nuevo' ? camaraNuevoRef : camaraDetalleRef).current?.click();
    } else {
      setCamaraDestino(destino);
    }
  };

  const onFotoCapturada = (file) => {
    const destino = camaraDestino;
    setCamaraDestino(null);
    if (destino === 'nuevo') {
      const nuevos = validarArchivos([file], archivos.length);
      if (nuevos) { setArchivos(a => [...a, ...nuevos]); toast.success('Foto agregada'); }
    } else if (destino === 'detalle') {
      subirAlDetalle([file]);
    }
  };

  const anular = async () => {
    if (largo(motivoAnular) < MIN_MOTIVO || anulando) return;
    if (!window.confirm(`¿Anular el gasto ${detalle.codigo} por ${formatCurrency(detalle.monto)}? Saldrá de los totales y no se puede deshacer.`)) return;
    setAnulando(true);
    try {
      const res = await gastosDiariosAPI.anular(detalle.id, motivoAnular.trim());
      if (res.data?.success) {
        toast.success(res.data.message || 'Gasto anulado');
        await abrirDetalle(detalle.id);
        cargar();
      }
    } catch (err) {
      toast.error(await mensajeError(err, 'No se pudo anular el gasto'));
    } finally {
      setAnulando(false);
    }
  };

  if (!autorizado) {
    return (
      <div className="alert alert-warning m-3">
        <i className="bi bi-lock me-2"></i>Solo administradores y gerentes pueden ver los gastos diarios.
      </div>
    );
  }

  const puedeEscribir = !modoEspectador;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h4 className="mb-0"><i className="bi bi-journal-text me-2"></i>Gastos Diarios</h4>
          <small className="text-muted">Gastos y compras del día con su comprobante. No afecta caja registradora ni caja chica.</small>
        </div>
        {puedeEscribir && (
          <button className="btn btn-danger" onClick={abrirNuevo}>
            <i className="bi bi-plus-lg me-1"></i>Registrar gasto
          </button>
        )}
      </div>

      {modoEspectador && (
        <div className="alert alert-info py-2"><i className="bi bi-eye me-2"></i>Modo espectador: solo lectura en esta sucursal.</div>
      )}

      {/* KPIs del período filtrado */}
      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <div className="stat-card" style={{ borderLeft: '4px solid #dc3545' }}>
            <p className="stat-label mb-1">Total del período</p>
            <h4 className="stat-value mb-0 text-danger">{totales ? formatCurrency(totales.total_registrado) : '—'}</h4>
            <small className="text-muted">{totales?.cantidad_registrados ?? 0} gasto(s) vigentes</small>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat-card" style={{ borderLeft: '4px solid #ffc107' }}>
            <p className="stat-label mb-1">Sin comprobante</p>
            <h4 className="stat-value mb-0 text-warning">{totales ? formatCurrency(totales.total_sin_comprobante) : '—'}</h4>
            <small className="text-muted">{totales?.sin_comprobante ?? 0} gasto(s) con motivo</small>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat-card" style={{ borderLeft: '4px solid #6c757d' }}>
            <p className="stat-label mb-1">Anulados</p>
            <h4 className="stat-value mb-0">{totales?.anulados ?? 0}</h4>
            <small className="text-muted">No suman en los totales</small>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="stat-card" style={{ borderLeft: '4px solid #0d6efd' }}>
            <p className="stat-label mb-1">Mayor categoría</p>
            <h6 className="mb-0 text-truncate">{porCategoria[0]?.categoria || '—'}</h6>
            <small className="text-muted">{porCategoria[0] ? formatCurrency(porCategoria[0].total) : ''}</small>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small mb-1">Buscar</label>
              <input type="search" className="form-control form-control-sm" placeholder="Código, concepto, proveedor, N° documento, usuario…"
                value={buscarInput} onChange={(e) => setBuscarInput(e.target.value)} />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small mb-1">Desde</label>
              <input type="date" className="form-control form-control-sm" value={filtros.fecha_desde}
                onChange={(e) => setFiltro('fecha_desde', e.target.value)} />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small mb-1">Hasta</label>
              <input type="date" className="form-control form-control-sm" value={filtros.fecha_hasta}
                onChange={(e) => setFiltro('fecha_hasta', e.target.value)} />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small mb-1">Categoría</label>
              <select className="form-select form-select-sm" value={filtros.categoria_id}
                onChange={(e) => setFiltro('categoria_id', e.target.value)}>
                <option value="">Todas</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-1">
              <label className="form-label small mb-1">Comprobante</label>
              <select className="form-select form-select-sm" value={filtros.comprobante}
                onChange={(e) => setFiltro('comprobante', e.target.value)}>
                <option value="">Todos</option>
                <option value="con">Con</option>
                <option value="sin">Sin</option>
              </select>
            </div>
            <div className="col-6 col-md-1">
              <label className="form-label small mb-1">Estado</label>
              <select className="form-select form-select-sm" value={filtros.estado}
                onChange={(e) => setFiltro('estado', e.target.value)}>
                <option value="">Todos</option>
                <option value="registrado">Vigentes</option>
                <option value="anulado">Anulados</option>
              </select>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-1 mt-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRango(fechaLocal(), fechaLocal())}>Hoy</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRango(haceDias(6), fechaLocal())}>Últimos 7 días</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRango(primeroDelMes(), fechaLocal())}>Este mes</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setRango('', '')}>Todo</button>
          </div>
        </div>
      </div>

      {/* Listado */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Fecha</th>
                <th>Código</th>
                <th>Concepto</th>
                <th>Categoría</th>
                <th>Proveedor</th>
                <th>Pago</th>
                <th className="text-end">Monto</th>
                <th>Comprobante</th>
                <th>Registró</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="text-center py-4"><span className="spinner-border spinner-border-sm me-2"></span>Cargando…</td></tr>
              ) : gastos.length === 0 ? (
                <tr><td colSpan="10" className="text-center text-muted py-4">No hay gastos con estos filtros</td></tr>
              ) : gastos.map(g => (
                <tr key={g.id} className={g.estado === 'anulado' ? 'text-muted' : ''} style={{ cursor: 'pointer' }} onClick={() => abrirDetalle(g.id)}>
                  <td className="text-nowrap">{fmtFecha(g.fecha)}</td>
                  <td className="text-nowrap"><strong>{g.codigo}</strong></td>
                  <td style={{ maxWidth: 260 }}>
                    <div className={`text-truncate ${g.estado === 'anulado' ? 'text-decoration-line-through' : ''}`}>{g.concepto}</div>
                    {g.estado === 'anulado' && <span className="badge bg-secondary">Anulado</span>}
                  </td>
                  <td>{g.categoria_nombre || <span className="text-muted">—</span>}</td>
                  <td className="text-truncate" style={{ maxWidth: 160 }}>{g.proveedor_mostrar || <span className="text-muted">—</span>}</td>
                  <td>{labelMetodo(g.metodo_pago)}</td>
                  <td className={`text-end fw-semibold ${g.estado === 'anulado' ? 'text-decoration-line-through' : ''}`}>{formatCurrency(g.monto)}</td>
                  <td>
                    {g.tiene_comprobante
                      ? <span className="badge bg-success"><i className="bi bi-paperclip"></i> {g.num_comprobantes}</span>
                      : <span className="badge bg-warning text-dark" title={g.motivo_sin_comprobante}>Sin comprobante</span>}
                  </td>
                  <td className="small">{g.usuario_nombre}</td>
                  <td><i className="bi bi-chevron-right text-muted"></i></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="card-footer bg-transparent d-flex justify-content-between align-items-center">
            <small className="text-muted">{pagination.total} registro(s)</small>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
              <span className="btn btn-outline-secondary disabled">{page} / {pagination.pages}</span>
              <button className="btn btn-outline-secondary" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Modal: Registrar gasto ─── */}
      {showNuevo && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title"><i className="bi bi-journal-plus me-2"></i>Registrar gasto diario</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowNuevo(false)} disabled={guardando} />
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Fecha del gasto *</label>
                      <input type="date" className="form-control" max={fechaLocal()} value={form.fecha}
                        onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Monto *</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input type="text" className={`form-control ${montoInvalido ? 'is-invalid' : ''}`} inputMode="decimal" placeholder="0,00"
                          value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} />
                      </div>
                      {montoInvalido && <small className="text-danger">Monto mayor a 0, máximo 2 decimales</small>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Método de pago *</label>
                      <select className="form-select" value={form.metodo_pago}
                        onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })}>
                        {METODOS_PAGO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Concepto *</label>
                      <input type="text" className="form-control" maxLength={300} placeholder="Ej: Agua y café para la óptica"
                        value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Categoría</label>
                      <select className="form-select" value={form.categoria_id}
                        onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}>
                        <option value="">Sin categoría</option>
                        {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Proveedor</label>
                      <select className="form-select" value={form.proveedor_sel}
                        onChange={(e) => setForm({ ...form, proveedor_sel: e.target.value })}>
                        <option value="">Sin proveedor</option>
                        <option value={PROVEEDOR_OTRO}>Otro (escribir nombre)…</option>
                        {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                      {form.proveedor_sel === PROVEEDOR_OTRO && (
                        <input type="text" className="form-control mt-2" maxLength={200} placeholder="Nombre del proveedor o tienda"
                          value={form.proveedor_nombre} onChange={(e) => setForm({ ...form, proveedor_nombre: e.target.value })} />
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">N° factura / documento</label>
                      <input type="text" className="form-control" maxLength={50}
                        value={form.numero_documento} onChange={(e) => setForm({ ...form, numero_documento: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Descripción</label>
                      <input type="text" className="form-control" maxLength={1000}
                        value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
                    </div>

                    <div className="col-12">
                      <label className="form-label small fw-semibold">Comprobante / factura (imagen o PDF, máx. {MAX_ARCHIVOS})</label>
                      <div>
                        <input ref={archivosRef} type="file" className="d-none" multiple
                          accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onElegirArchivos} />
                        <input ref={camaraNuevoRef} type="file" className="d-none"
                          accept="image/*" capture="environment" onChange={onElegirArchivos} />
                        <div className="d-flex flex-wrap gap-2">
                          <button type="button" className="btn btn-primary btn-sm" disabled={archivos.length >= MAX_ARCHIVOS}
                            onClick={() => tomarFoto('nuevo')}>
                            <i className="bi bi-camera me-1"></i>Tomar foto
                          </button>
                          <button type="button" className="btn btn-outline-primary btn-sm" disabled={archivos.length >= MAX_ARCHIVOS}
                            onClick={() => archivosRef.current?.click()}>
                            <i className="bi bi-paperclip me-1"></i>Adjuntar archivo
                          </button>
                        </div>
                      </div>
                      {archivos.length > 0 && (
                        <ul className="list-group list-group-flush mt-2">
                          {archivos.map((f, i) => (
                            <li key={i} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                              <small><i className={`bi ${f.type === 'application/pdf' ? 'bi-file-earmark-pdf text-danger' : 'bi-image text-primary'} me-1`}></i>{f.name} <span className="text-muted">({(f.size / 1024 / 1024).toFixed(2)} MB)</span></small>
                              <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => setArchivos(a => a.filter((_, j) => j !== i))}>Quitar</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {archivos.length === 0 && (
                      <div className="col-12">
                        <div className="alert alert-warning py-2 mb-2 small">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Sin comprobante: es <strong>obligatorio</strong> explicar por qué no hay factura o recibo.
                        </div>
                        <label className="form-label small fw-semibold">Motivo de no tener comprobante *</label>
                        <textarea className={`form-control ${form.motivo_sin_comprobante && !motivoOk ? 'is-invalid' : ''}`} rows={2} maxLength={500}
                          placeholder="Ej: Tienda de barrio no entrega factura"
                          value={form.motivo_sin_comprobante} onChange={(e) => setForm({ ...form, motivo_sin_comprobante: e.target.value })} />
                        <small className={motivoOk ? 'text-success' : 'text-muted'}>
                          {largo(form.motivo_sin_comprobante)}/{MIN_MOTIVO} caracteres mínimo
                        </small>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowNuevo(false)} disabled={guardando}>Cancelar</button>
                  <button className="btn btn-danger" onClick={guardar} disabled={!formValido || guardando}>
                    {guardando ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando…</> : <><i className="bi bi-check-lg me-1"></i>Registrar gasto</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Modal: Detalle ─── */}
      {detalle && (
        <>
          <div className="modal-backdrop fade show" onClick={cerrarDetalle} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-journal-text me-2"></i>{detalle.codigo || 'Gasto'}
                    {detalle.estado === 'anulado' && <span className="badge bg-secondary ms-2">Anulado</span>}
                  </h5>
                  <button className="btn-close" onClick={cerrarDetalle} />
                </div>
                <div className="modal-body">
                  {loadingDetalle || !detalle.codigo ? (
                    <div className="text-center py-4"><span className="spinner-border"></span></div>
                  ) : (
                    <>
                      <div className="row g-2 small mb-3">
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Fecha</span>{fmtFecha(detalle.fecha)}</div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Monto</span><strong className="fs-6">{formatCurrency(detalle.monto)}</strong></div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Método de pago</span>{labelMetodo(detalle.metodo_pago)}</div>
                        <div className="col-12"><span className="text-muted d-block">Concepto</span>{detalle.concepto}</div>
                        {detalle.descripcion && <div className="col-12"><span className="text-muted d-block">Descripción</span>{detalle.descripcion}</div>}
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Categoría</span>{detalle.categoria_nombre || '—'}</div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Proveedor</span>{detalle.proveedor_mostrar || '—'}</div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">N° documento</span>{detalle.numero_documento || '—'}</div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Registró</span>{detalle.usuario_nombre}</div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Registrado el</span>{fmtFechaHora(detalle.created_at)}</div>
                        <div className="col-6 col-md-4"><span className="text-muted d-block">Sucursal</span>{detalle.sucursal_nombre}</div>
                      </div>

                      {detalle.motivo_sin_comprobante && (
                        <div className="alert alert-warning py-2 small">
                          <strong>Motivo sin comprobante{detalle.tiene_comprobante ? ' (al registrar)' : ''}:</strong> {detalle.motivo_sin_comprobante}
                        </div>
                      )}

                      {detalle.estado === 'anulado' && (
                        <div className="alert alert-secondary py-2 small">
                          <strong>Anulado</strong> por {detalle.anulado_por_nombre} el {fmtFechaHora(detalle.fecha_anulacion)}.<br />
                          <strong>Motivo:</strong> {detalle.motivo_anulacion}
                        </div>
                      )}

                      <h6 className="fw-bold mt-3"><i className="bi bi-paperclip me-1"></i>Comprobantes ({detalle.comprobantes?.length || 0})</h6>
                      {(detalle.comprobantes || []).length === 0 && <p className="text-muted small">Sin comprobantes adjuntos.</p>}
                      <ul className="list-group mb-2">
                        {(detalle.comprobantes || []).map(c => (
                          <li key={c.id} className="list-group-item">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                              <small>
                                <i className={`bi ${c.archivo_tipo === 'application/pdf' ? 'bi-file-earmark-pdf text-danger' : 'bi-image text-primary'} me-1`}></i>
                                {c.archivo_nombre_original}
                                <span className="text-muted"> · {c.subido_por_nombre} · {fmtFechaHora(c.created_at)}</span>
                              </small>
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-outline-primary" onClick={() => verComprobante(c)}>
                                  <i className={`bi ${preview?.compId === c.id ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>{preview?.compId === c.id ? 'Ocultar' : 'Ver'}
                                </button>
                                <button className="btn btn-outline-secondary" onClick={() => descargarComprobante(c)}>
                                  <i className="bi bi-download me-1"></i>Descargar
                                </button>
                              </div>
                            </div>
                            {preview?.compId === c.id && (
                              <div className="mt-2 text-center">
                                {preview.tipo === 'application/pdf'
                                  ? <iframe src={preview.url} title="Comprobante" style={{ width: '100%', height: 480, border: 0 }} />
                                  : <img src={preview.url} alt="Comprobante" className="img-fluid rounded border" style={{ maxHeight: 480 }} />}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>

                      {puedeEscribir && detalle.estado === 'registrado' && (detalle.comprobantes?.length || 0) < MAX_ARCHIVOS && (
                        <>
                          <input ref={agregarRef} type="file" className="d-none" multiple
                            accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onAgregarComprobantes} />
                          <input ref={camaraDetalleRef} type="file" className="d-none"
                            accept="image/*" capture="environment" onChange={onAgregarComprobantes} />
                          {subiendo ? (
                            <span className="small"><span className="spinner-border spinner-border-sm me-1"></span>Subiendo…</span>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              <button className="btn btn-primary btn-sm" onClick={() => tomarFoto('detalle')}>
                                <i className="bi bi-camera me-1"></i>Tomar foto
                              </button>
                              <button className="btn btn-outline-primary btn-sm" onClick={() => agregarRef.current?.click()}>
                                <i className="bi bi-paperclip me-1"></i>Adjuntar archivo
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {puedeEscribir && detalle.estado === 'registrado' && (
                        <div className="border-top mt-4 pt-3">
                          <h6 className="fw-bold text-danger"><i className="bi bi-x-circle me-1"></i>Anular gasto</h6>
                          <textarea className="form-control form-control-sm mb-1" rows={2} maxLength={500}
                            placeholder="Motivo de la anulación (obligatorio)"
                            value={motivoAnular} onChange={(e) => setMotivoAnular(e.target.value)} />
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">{largo(motivoAnular)}/{MIN_MOTIVO} caracteres mínimo</small>
                            <button className="btn btn-outline-danger btn-sm" onClick={anular}
                              disabled={largo(motivoAnular) < MIN_MOTIVO || anulando}>
                              {anulando ? 'Anulando…' : 'Anular'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {camaraDestino && (
        <CamaraModal onCaptura={onFotoCapturada} onCerrar={() => setCamaraDestino(null)} />
      )}
    </div>
  );
};

export default GastosDiarios;
